import React from 'react';

import { usePermissions } from '../context/PermissionContext';
import { BUSY_FILES_PERMISSIONS } from '../constants/reports';
import { ROUTES } from '../constants/routes';
import {
  BUSY_SERIES,
  BUSY_SERIES_RULE,
  BUSY_SHEETS,
  BUSY_COLUMNS,
  BUSY_READ_ONLY_SHEETS,
  BUSY_SUMMARY_SHEET,
  BUSY_CHECKS_SHEET,
  BUSY_TRADEMARK_NOTE,
} from '../constants/busyFile';
import AdminPageHeader from '../components/common/AdminPageHeader';

/**
 * BusyImportGuidePage (BUSY-VOUCHER R3, FE-GUIDE / story R3-S8) — the
 * "How to import into Busy" page, opened from the Monthly files section.
 *
 * - Same permission as that section: DOWNLOAD_BUSY_FILES (ACCOUNTANT + ADMIN,
 *   OR-84 — its own permission, not the Reports & Accounts ones) through
 *   `usePermissions()` — no role literals. Anyone else sees the
 *   one-line no-access panel (copy G3), same pattern as MonthlyCollectionsPage.
 * - Part 1 states OUR file exactly. The sheet list and the column list are
 *   rendered from `constants/busyFile.js` — the one source shared with the
 *   Monthly files "What's inside" — never retyped here. Every column must have
 *   a meaning in COLUMN_MEANING below (a test fails when a column is added
 *   without one).
 * - Part 2 is GENERIC: no Busy menu path, no setting name, no claim about
 *   Busy's behaviour (facts only; verified steps come later, stamped
 *   "Checked on Busy {version}, {date}").
 * - Wording is the FINAL wording of R3-copy-review.md (reviewer + OWNER EDITS).
 * - Pure render: no API call, no effect.
 */

const NO_ACCESS_TEXT = "You don't have access to this report.";

const PART_1_ID = 'guide-part-1';
const PART_2_ID = 'guide-part-2';
const PART_1_TITLE = 'Part 1 · Your file';
const PART_2_TITLE = 'Part 2 · Setting up Busy';

const SHEETS_ID = 'guide-sheets-section';
const COLUMNS_ID = 'guide-columns-section';
const HAND_ENTRY_ID = 'guide-hand-entry';
const SHEETS_TITLE = `The ${BUSY_SHEETS.length} sheets`;
const COLUMNS_TITLE = `The ${BUSY_COLUMNS.length} columns`;
const HAND_ENTRY_TITLE = 'Months already typed into Busy';

// Jump chips, in page order; each label is the heading it jumps to.
const JUMP_LINKS = [
  { id: PART_1_ID, label: PART_1_TITLE },
  { id: SHEETS_ID, label: SHEETS_TITLE },
  { id: COLUMNS_ID, label: COLUMNS_TITLE },
  { id: HAND_ENTRY_ID, label: HAND_ENTRY_TITLE },
  { id: PART_2_ID, label: PART_2_TITLE },
];

// Summary / Checks: what each is for (beside its name in the sheet list).
const READ_ONLY_NOTE_OF_SHEET = Object.fromEntries(
  BUSY_READ_ONLY_SHEETS.map((readOnly) => [readOnly.sheet, readOnly.note]),
);

// Refunded = every row on the Refunds sheets (the Summary sheet's own note).
const SUMMARY_REFUNDED_TEXT =
  'Refunded is every row on the Refunds sheets: refunds, cancelled (voided) payments, discounts after payment, '
  + 'adjustments and IPD deposits returned.';

// Refunds sheets are named "<Section> Refunds" (the file builder's rule).
const isRefundsSheet = (sheet) => sheet.endsWith('Refunds');

const SERIES_PREFIX_OF_SHEET = Object.fromEntries(
  BUSY_SERIES.map((series) => [series.sheet, series.prefix]),
);
const RECEIPT_SHEETS = BUSY_SERIES.map((series) => series.sheet).filter((sheet) => !isRefundsSheet(sheet));

// What each column holds (facts from the file builder; R3-copy-review G7).
const COLUMN_MEANING = {
  Sr: 'Row number on that sheet, starting at 1.',
  Date: "The voucher's date, DD/MM/YYYY.",
  'Voucher Type': 'Receipt on a receipts sheet; Payment on a Refunds sheet.',
  'Voucher No.': 'The voucher number, like OPD/26-27/00001.',
  'Receipt No.': "The number printed on the patient's receipt or credit note.",
  Section: "The sheet's name without 'Refunds'.",
  'Patient Name': "The patient's name.",
  UHID: "The patient's UHID. A patient without one shows UHID/ and the last 5 characters of their patient ID.",
  'Doctor Name': "The doctor's name without 'Dr'. SELF when no doctor advised a lab or radiology test. Blank for OTC.",
  Department: 'The department the payment belongs to. Blank when it has none.',
  Amount: 'In rupees. Always positive, with 2 decimals.',
  'Payment Mode': 'Cash, Card, UPI, QR, Net banking, Wallet or Other.',
  'Against Receipt':
    "Refunds sheets only: the receipt number of the payment being refunded or cancelled. "
    + "Blank on 'Adjustment' rows and when the original payment can't be found.",
  'Against Voucher':
    'Refunds sheets only: the voucher number of that payment. Also blank when the original payment has no voucher '
    + '(for example, it was paid before your books started).',
  Narration:
    'Blank on an OPD, OTC, Lab or Radiology receipt. Otherwise fixed wording only: Refund · '
    + 'Reversal of … (voided DD/MM/YYYY) · Deposit — IP … · Dues — IP … · Deposit returned — IP … · '
    + "Discount (after payment) · Adjustment. A late row reads 'Late entry for DD/MM/YYYY', or adds it after the "
    + "wording (for example 'Refund — Late entry for 23/10/2026'). No test names or diagnosis.",
};

const PART_2_POINTS = [
  'Try it in a test company first.',
  `Set up one voucher series for each sheet except ${BUSY_SUMMARY_SHEET} and ${BUSY_CHECKS_SHEET}.`,
  'Ask your Busy partner how Busy can keep the voucher numbers from the file (they must not be renumbered).',
  'Your accountant chooses the ledger for each section and payment mode.',
  `Don't import ${BUSY_SUMMARY_SHEET} or ${BUSY_CHECKS_SHEET}; compare totals with ${BUSY_CHECKS_SHEET} afterwards.`,
];

const PART_2_VERSION_NOTE = 'Exact menus differ by Busy version — see Busy help or your Busy partner.';

const cardClass =
  'mb-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800';
const bodyClass = 'text-sm leading-relaxed text-slate-700 dark:text-slate-200';
const mutedClass = 'text-xs text-slate-500 dark:text-slate-400';
const linkClass =
  'rounded-lg border border-teal-600 bg-white px-3 py-1.5 text-sm font-medium text-teal-800 hover:bg-teal-50 '
  + 'dark:border-teal-400 dark:bg-slate-800 dark:text-teal-200 dark:hover:bg-slate-700';

function Block({ title, id, children }) {
  return (
    <section id={id} className="mb-5 scroll-mt-4 last:mb-0">
      <h3 className="mb-1.5 text-sm font-semibold text-slate-900 dark:text-slate-50">{title}</h3>
      {children}
    </section>
  );
}

function SheetList() {
  return (
    <ol data-testid="guide-sheets" className="mb-2 divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
      {BUSY_SHEETS.map((sheet, index) => {
        const prefix = SERIES_PREFIX_OF_SHEET[sheet];
        return (
          <li
            key={sheet}
            data-testid="guide-sheet"
            className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-sm"
          >
            <span className="w-5 shrink-0 text-xs text-slate-500 dark:text-slate-400">{index + 1}</span>
            <span data-testid="guide-sheet-name" className="font-medium text-slate-900 dark:text-slate-50">{sheet}</span>
            {prefix ? (
              <span className={mutedClass}>
                voucher series <span className="font-mono">{prefix}/</span>
              </span>
            ) : (
              <span className={mutedClass}>{READ_ONLY_NOTE_OF_SHEET[sheet]}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ColumnList() {
  return (
    <dl data-testid="guide-columns" className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
      {BUSY_COLUMNS.map((column, index) => (
        <div
          key={column}
          data-testid="guide-column"
          className="grid grid-cols-1 gap-x-3 gap-y-0.5 px-3 py-2 text-sm sm:grid-cols-[10rem_1fr]"
        >
          <dt data-testid="guide-column-name" className="font-medium text-slate-900 dark:text-slate-50">
            <span className="mr-2 text-xs font-normal text-slate-500 dark:text-slate-400">{index + 1}</span>
            {column}
          </dt>
          <dd className="min-w-0 break-words text-slate-700 dark:text-slate-200">{COLUMN_MEANING[column]}</dd>
        </div>
      ))}
    </dl>
  );
}

function PartOne() {
  return (
    <section id={PART_1_ID} data-testid="guide-part-1" className={`${cardClass} scroll-mt-4`}>
      <h2 className="mb-3 text-base font-semibold text-slate-900 dark:text-slate-50">{PART_1_TITLE}</h2>

      <Block id={SHEETS_ID} title={SHEETS_TITLE}>
        <p className={`${bodyClass} mb-2`}>
          Each month&apos;s file has {BUSY_SHEETS.length} sheets, in this order.
        </p>
        <SheetList />
        <p className={bodyClass}>
          {BUSY_SERIES_RULE} Row 1 is the header; entries start on row 2. Empty sheets keep their header row.
        </p>
      </Block>

      <Block id={COLUMNS_ID} title={COLUMNS_TITLE}>
        <p className={`${bodyClass} mb-2`}>
          Every sheet except {BUSY_SUMMARY_SHEET} and {BUSY_CHECKS_SHEET} has the same {BUSY_COLUMNS.length} columns.
        </p>
        <ColumnList />
      </Block>

      <Block title="Receipts and refunds">
        <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
          <li>
            Receipt sheets ({RECEIPT_SHEETS.join(', ')}): Voucher Type is Receipt. Against Receipt and Against Voucher are empty.
          </li>
          <li>
            Refunds sheets: Voucher Type is Payment. Against Receipt and Against Voucher point to the original payment
            (see the columns above).
          </li>
          <li>Amounts are always positive, with 2 decimals.</li>
          <li>Dates are DD/MM/YYYY.</li>
        </ul>
      </Block>

      <Block title="Voucher numbers">
        <p className={bodyClass}>
          Voucher numbers look like <span className="font-mono">OPD/26-27/00001</span> (at most 16 characters).
          Numbering restarts at 00001 on 1 April each financial year and carries on month to month.
        </p>
      </Block>

      <Block title="The Summary sheet">
        <p className={bodyClass}>
          The {BUSY_SUMMARY_SHEET} sheet shows the month&apos;s totals by section and by payment mode: Received,
          Refunded and Net. {SUMMARY_REFUNDED_TEXT} Do not import the {BUSY_SUMMARY_SHEET} sheet; it is for reading only.
        </p>
      </Block>

      <Block title="The Checks sheet">
        <p className={bodyClass}>
          Do not import the {BUSY_CHECKS_SHEET} sheet. After import, compare Busy&apos;s total for each series with the{' '}
          {BUSY_CHECKS_SHEET} sheet.
        </p>
      </Block>

      <Block title="A file never changes">
        <p className={bodyClass}>
          Once a month&apos;s file is made it never changes. A payment entered late, or cancelled after its file was made,
          appears as a new row in a later month&apos;s file (&apos;Late entry for DD/MM/YYYY&apos; or &apos;Reversal of …&apos;).
        </p>
      </Block>

      <Block id={HAND_ENTRY_ID} title={HAND_ENTRY_TITLE}>
        <p
          data-testid="guide-hand-entry-warning"
          className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
        >
          If your accountant already typed any of these months into Busy by hand, those entries will not match this file.
          Decide with your accountant whether to keep them or use the file instead, so nothing is counted twice.
        </p>
      </Block>

      <Block title="If your books started mid-month">
        <p className={bodyClass}>
          If your books started in the middle of a month, that month&apos;s file covers only the days from the start date.
        </p>
      </Block>

      <Block title="Not in the file">
        <p className={bodyClass}>
          Not in the file: insurance claim notes (INS/ numbers), waivers/concessions, doctor payouts.
        </p>
      </Block>

      <Block title="Registration fee">
        <p className={bodyClass}>
          On the printed Lab &amp; Diagnostic queue sheet a new patient&apos;s registration fee is shown on the same row as
          their test (Lab or Radiology). In the Collections board and the books it is counted under OPD.
        </p>
      </Block>

      <Block title="IPD deposits">
        <p className={bodyClass}>
          The IPD sheet includes deposits (narration &apos;Deposit — IP …&apos;). Your accountant decides whether to book
          them as income or as an advance.
        </p>
      </Block>
    </section>
  );
}

function PartTwo() {
  return (
    <section id={PART_2_ID} data-testid="guide-part-2" className={`${cardClass} scroll-mt-4`}>
      <h2 className="mb-3 text-base font-semibold text-slate-900 dark:text-slate-50">{PART_2_TITLE}</h2>
      <p data-testid="guide-part-2-note" className={`${bodyClass} mb-2 font-medium`}>{PART_2_VERSION_NOTE}</p>
      <ul data-testid="guide-part-2-points" className={`${bodyClass} list-disc space-y-1 pl-5`}>
        {PART_2_POINTS.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </section>
  );
}

export default function BusyImportGuidePage() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(BUSY_FILES_PERMISSIONS.DOWNLOAD_BUSY_FILES);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader backTo={ROUTES.MONTHLY_COLLECTIONS} backLabel="← Reports & Accounts" />
      <div className="mx-auto max-w-3xl p-4" data-testid="busy-import-guide-page">
        {canView ? (
          <>
            <h1 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-50">How to import into Busy</h1>
            <p className={`${bodyClass} mb-3`}>
              Part 1 describes our file exactly. Part 2 gives general steps for Busy only.
            </p>
            <nav aria-label="On this page" data-testid="guide-jump-links" className="mb-4 flex flex-wrap gap-2">
              {JUMP_LINKS.map((link) => (
                <a key={link.id} href={`#${link.id}`} className={linkClass}>{link.label}</a>
              ))}
            </nav>
            <PartOne />
            <PartTwo />
            <p data-testid="guide-trademark-note" className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              {BUSY_TRADEMARK_NOTE}
            </p>
          </>
        ) : (
          <div role="alert" data-testid="no-access" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
            <h1 className="mb-1 text-lg font-semibold">How to import into Busy</h1>
            <p className="text-sm">{NO_ACCESS_TEXT}</p>
          </div>
        )}
      </div>
    </div>
  );
}
